import { useCallback } from "react";
import type { RefObject } from "react";
import { toast } from "sonner";
import type { EditableDataGridHandle } from "@/components/editable-data-grid";

interface UseSubmittalBulkActionsArgs {
  gridRef: RefObject<EditableDataGridHandle | null>;
  selectedRows: Set<number>;
  setSelectedRows: (rows: Set<number>) => void;
  csiCodeCol: number;
  csiTitleCol: number;
  submittalManagerCol: number;
  displayRows?: string[][];
  packageSpecSectionNumberCol: number;
  packageSpecSectionDescriptionCol: number;
  submittalNumberCol: number;
}

export function useSubmittalBulkActions({
  gridRef,
  selectedRows,
  setSelectedRows,
  csiCodeCol,
  csiTitleCol,
  submittalManagerCol,
  displayRows,
  packageSpecSectionNumberCol,
  packageSpecSectionDescriptionCol,
  submittalNumberCol,
}: UseSubmittalBulkActionsArgs) {
  const handleBulkCSIAssign = useCallback(
    (code: string, title: string, divisionCode?: string, divisionTitle?: string) => {
      if (!gridRef.current || selectedRows.size === 0) return;

      const allRows = gridRef.current.getRows() ?? displayRows;
      const updates: Array<{ row: number; col: number; value: string }> = [];
      const affectedCodes = new Set<string>();

      for (const rowIdx of selectedRows) {
        const oldCode = allRows?.[rowIdx]?.[csiCodeCol]?.trim() ?? "";
        const newCode = code.trim();
        
        if (csiCodeCol >= 0) {
          updates.push({ row: rowIdx, col: csiCodeCol, value: code });
        }
        if (csiTitleCol >= 0) {
          updates.push({ row: rowIdx, col: csiTitleCol, value: title });
        }
        // Update Package Spec Section columns with division info
        if (divisionCode && packageSpecSectionNumberCol >= 0) {
          updates.push({ row: rowIdx, col: packageSpecSectionNumberCol, value: divisionCode });
        }
        if (divisionTitle && packageSpecSectionDescriptionCol >= 0) {
          updates.push({ row: rowIdx, col: packageSpecSectionDescriptionCol, value: divisionTitle });
        }
        
        if (oldCode !== newCode) {
          affectedCodes.add(oldCode);
          affectedCodes.add(newCode);
        }
      }

      // Update submittal numbers for affected codes
      if (submittalNumberCol >= 0 && affectedCodes.size > 0) {
        const sequences: Record<string, number> = {};
        const newCode = code.trim();
        for (let i = 0; i < (allRows?.length ?? 0); i++) {
          // For selected rows, use the new code; for others, use existing code
          const rowCsi = selectedRows.has(i) ? newCode : (allRows?.[i]?.[csiCodeCol] ?? "").trim();
          if (!rowCsi || !affectedCodes.has(rowCsi)) continue;
          sequences[rowCsi] = (sequences[rowCsi] ?? 0) + 1;
          updates.push({
            row: i,
            col: submittalNumberCol,
            value: `${rowCsi}-${sequences[rowCsi]}`,
          });
        }
      }

      gridRef.current.updateCells(updates);
      setSelectedRows(new Set());
      toast.success(
        `Assigned ${code} to ${selectedRows.size} row${selectedRows.size !== 1 ? "s" : ""}`
      );
    },
    [selectedRows, csiCodeCol, csiTitleCol, packageSpecSectionNumberCol, packageSpecSectionDescriptionCol, submittalNumberCol, gridRef, setSelectedRows, displayRows]
  );

  const handleBulkColumnAssign = useCallback(
    (colIndex: number, value: string, columnName: string) => {
      if (!gridRef.current || selectedRows.size === 0 || colIndex < 0) return;

      const updates: Array<{ row: number; col: number; value: string }> = [];
      for (const rowIdx of selectedRows) {
        updates.push({ row: rowIdx, col: colIndex, value });
      }
      gridRef.current.updateCells(updates);
      toast.success(
        `Set ${columnName} to "${value}" for ${selectedRows.size} row${selectedRows.size !== 1 ? "s" : ""}`
      );
    },
    [selectedRows, gridRef]
  );

  const handleSubmittalManagerChange = useCallback(
    (managerName: string) => {
      if (!gridRef.current || submittalManagerCol < 0 || !displayRows) return;

      const updates: Array<{ row: number; col: number; value: string }> = [];
      
      // If rows are selected, only apply to selected rows
      if (selectedRows.size > 0) {
        for (const rowIdx of selectedRows) {
          updates.push({ row: rowIdx, col: submittalManagerCol, value: managerName });
        }
        toast.success(`Set Submittal Manager to "${managerName}" for ${selectedRows.size} selected row${selectedRows.size !== 1 ? 's' : ''}`);
      } else {
        // No selection - apply to all rows
        for (let rowIdx = 0; rowIdx < displayRows.length; rowIdx++) {
          updates.push({ row: rowIdx, col: submittalManagerCol, value: managerName });
        }
        toast.success(`Set Submittal Manager to "${managerName}" for all rows`);
      }
      
      gridRef.current.updateCells(updates);
    },
    [displayRows, submittalManagerCol, gridRef, selectedRows]
  );

  return {
    handleBulkCSIAssign,
    handleBulkColumnAssign,
    handleSubmittalManagerChange,
  };
}
